import {
  Label, ComboBox,
  RecordInterface, SetFormValues,
} from '@components/common/Forms';
import { Dispatch, SetStateAction } from 'react';
import { useTranslation } from 'next-i18next';
import { PencilSquareIcon } from '@heroicons/react/24/outline';

/** Props */
interface JobDomainSuggestProps {
  record: RecordInterface,
  setFormValues: SetFormValues,
  domainInputName?: string,
  labelDescriptionClass?: string,
  occupationSetter?: Dispatch<SetStateAction<string | undefined >>,
  parentDomainSetter?: Dispatch<SetStateAction<string | undefined>>,
  showDomainSuggestSetter?: Dispatch<SetStateAction<boolean>>,
  setHasSelectedDomain?: Dispatch<SetStateAction<boolean>>,
}

/**
 * @description Job Domain Suggest component. To be used in a form.
 * - `record` - record function from useBjForm
 * - `setFormValues` - function to update the current form values state
 * - `labelDescriptionClass` - additional classes for the label description
 * - `domainInputName` - name of the domain input; default is 'domain'
 * - `occupationSetter` - occupation name setter
 * - `parentDomainSetter` - parent domain name setter
 * - `showDomainSuggestSetter` - show domain suggest setter
 * - `setHasSelectedDomain` - set has selected domain setter
 */
export const JobDomainSuggest = (props: JobDomainSuggestProps) => {
  // Destructure props
  const {
    record,
    setFormValues,
    domainInputName = 'domain',
    labelDescriptionClass = '',
    occupationSetter,
    parentDomainSetter,
    showDomainSuggestSetter,
    setHasSelectedDomain,
  } = props;

  // Translation
  const { t } = useTranslation();

  // Render
  // **************************
  return (
    <Label
      as="div"
      description={t('job.add.autocomplete_assigned_domain_label')}
      descriptionClass={labelDescriptionClass}
    >
      <div className="relative">
        <PencilSquareIcon
          className="absolute left-3 top-6 z-1 size-5 -translate-y-1/2 text-ink"
        />

        <ComboBox
          className="text-ellipsis pl-10 pr-18"
          minLength={3}
          options={{
            valueFromKey: 'name',
            freeSolo: false,
            apiList: {
              fetchUrl: '/v1/job-operation/domain/autocomplete',
              queryParams: (query: string) => ({
                term: query,
              }),
            },
          }}
          renderList={(
            item,
            index,
            listKey,
            getItemProps,
            listStyling,
            highlightedIndex,
          ) => {
            const domain = item as ApiSchema<'DomainAutocompleteItem'>;
            const highlightedClass = highlightedIndex === index ? 'text-white' : 'text-ink-medium';
            return (
              <li key={listKey} className={`${listStyling} flex-col`} {...getItemProps({ item, index })}>
                <span>{domain?.text}</span>
                <span className={`${highlightedClass} -mt-0.5 text-tiny font-normal transition-all duration-150`}>
                  {domain?.parent}
                </span>
              </li>
            );
          }}
          onSelectedItemChange={(selectedItem) => {
            // set the domain in the form on selection
            const domain = selectedItem as ApiSchema<'DomainAutocompleteItem'>;
            if (domain && domain?.id) {
              setFormValues({
                [domainInputName]: domain.id,
              });
              occupationSetter?.(domain.text);
              parentDomainSetter?.(domain.parent);
              showDomainSuggestSetter?.(false);
              setHasSelectedDomain?.(true);
            }
          }}
          {...record('domainSearch', {
            removeFromSubmit: true,
          })}
        />
      </div>
    </Label>
  );
};
